import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Pencil, Trash2, Loader2, Users2 } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABEL, ROLE_OPTIONS } from "@/lib/workflow";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "creator" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch((e) => { toast.error(apiError(e)); setUsers([]); });
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "creator" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!editing && (!form.email.trim() || !form.password)) return toast.error("Name, email and password are required");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name.trim(), role: form.role, email: form.email.trim().toLowerCase() };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...form, email: form.email.trim().toLowerCase(), name: form.name.trim() });
        toast.success("User created");
      }
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "creator" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users2 className="w-5 h-5" /> Team &amp; Roles
          </h2>
          <p className="text-sm text-slate-500">Staff create &amp; edit vouchers · Admins approve, pay, lock/unlock.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-user-btn"><UserPlus className="w-4 h-4" /> Add User</Button>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        {users === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm text-slate-500 font-mono">{u.email}</TableCell>
                  <TableCell>
                    <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold border ${
                      u.role === "admin"
                        ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900"
                        : "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300"
                    }`}>{ROLE_LABEL[u.role] || u.role}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {u.id !== me?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-user-${u.email}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>This removes their access immediately.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(u)} className="bg-rose-600 hover:bg-rose-500">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.name}` : "Add user"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="user-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{editing ? "New password" : "Password"}</Label>
                <Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5"
                  placeholder={editing ? "Leave blank to keep" : ""} data-testid="user-password" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Role</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (<SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
